import { amplifyConfiguration } from '@api/cognito/amplifyConfiguration'
import { AUTH_STATE } from '@enums/auth-state'
import { useAuthState } from '@contexts/AuthState'
import { useOktaAuth } from '@okta/okta-react'
import { trackCustomPageEvent } from '@api/segment/page'
import { SegmentPage } from '@enums/segment'
import { OktaSignFlow, signIn } from '@api/okta'
import {
  CognitoUser,
  forgotPasswordTriggerEmail,
  forgotPasswordUpdatePassword,
  signIn as cognitoSignIn,
  signOut,
  signUp as cognitoSignup,
} from '@api/cognito'
import { SUBMISSION_ERR } from '@enums/form-submission-error'
import isNull from 'lodash.isnull'
import { ApiError } from '@util/api-error'
import { captureException as sentryCaptureException } from '@api/sentry'
import { useState } from 'react'
import { Auth } from 'aws-amplify'

export const handleError = (error: ApiError) => {
  switch (error.message) {
    case SUBMISSION_ERR.NONEXISTENT_USER:
    case SUBMISSION_ERR.INCORRECT_USER_PWORD:
    case SUBMISSION_ERR.MAX_SIGN_UP_SIGN_IN_ATTEMPTS:
    case SUBMISSION_ERR.CLIENT_NOT_FOUND:
      return
    default:
      sentryCaptureException(error)
  }
}

const useAuth = () => {
  const appAuthState = useAuthState()
  const oktaA = useOktaAuth()
  const [isLoading, setLoading] = useState(false)

  const emailSignIn = async (email: string): Promise<{ isOkta: boolean }> => {
    setLoading(true)
    const cleanEmail = email.toLowerCase().trim()
    const configuration = await amplifyConfiguration.configureViaEmail(
      cleanEmail
    )
    trackCustomPageEvent(SegmentPage.ssoSignIn)
    try {
      await signIn(configuration, { flow: OktaSignFlow.logIn })
      return { isOkta: !isNull(configuration.oktaConfiguration) }
    } catch (error) {
      handleError(error)
      throw new Error(error)
    } finally {
      setLoading(false)
    }
  }

  const passwordSignIn = async (
    email: string,
    password: string
  ): Promise<CognitoUser | Error> => {
    setLoading(true)
    try {
      return await cognitoSignIn({ email, password })
    } catch (error) {
      handleError(error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const sendResetPasswordEmail = async (
    email: string
  ): Promise<any | Error> => {
    setLoading(true)
    try {
      return await forgotPasswordTriggerEmail({ email })
    } catch (error) {
      handleError(error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const resetPassword = async (
    email: string,
    password: string,
    verificationCode: string
  ): Promise<any | Error> => {
    setLoading(true)
    try {
      return await forgotPasswordUpdatePassword({
        email,
        verificationCode,
        password,
      })
    } catch (error) {
      handleError(error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const signup = async (params: {
    email: string
    password: string
    companyHint: string
    firstName: string
    lastName: string
  }): Promise<any | Error> => {
    setLoading(true)
    try {
      return await cognitoSignup(params)
    } catch (error) {
      handleError(error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const logout = async () => {
    const isOktaAuthenticated = await oktaA.oktaAuth.isAuthenticated()
    if (isOktaAuthenticated) {
      await oktaA.oktaAuth.tokenManager.clear()
    } else {
      await signOut()
    }
  }

  const isAuthenticated = (): boolean => {
    if (isNull(appAuthState)) {
      return false
    } else {
      return !!(
        appAuthState === AUTH_STATE.AUTHD || oktaA.authState.isAuthenticated
      )
    }
  }

  const getCompanyId = async () => {
    const isOktaAuthenticated = await oktaA.oktaAuth.isAuthenticated()
    if (isOktaAuthenticated) {
      const idToken = await oktaA.oktaAuth.getIdToken()
      if (idToken) {
        const decodedToken = oktaA.oktaAuth.token.decode(idToken)
        return decodedToken.payload['custom:company_id']
      } else {
        return ''
      }
    }
    const i = await Auth.currentSession()
    const c = i.getIdToken().decodePayload()
    return c['custom:company_id']
  }

  return {
    emailSignIn,
    passwordSignIn,
    isAuthenticated,
    sendResetPasswordEmail,
    resetPassword,
    signup,
    logout,
    isLoading,
    getCompanyId,
  }
}

export default useAuth
